import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { api } from '../../services/api';
import { useLang } from '../../i18n/context';

type Phase = 'upload' | 'cropping' | 'analyzing' | 'results' | 'success';

interface OcrData {
  title?: string; date?: string; venue?: string; city?: string;
  start_time?: string; end_time?: string; price?: string;
  is_free?: boolean; artists?: string[]; image_url?: string; ocr_source?: string;
  event_id?: string;
}

interface FlyerOcrModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const DUB_PHRASES = [
  'Mixing the riddim...', 'Loading the dubplates...', 'Selecting the frequency...',
  'Pull up, rewind!', 'Heavy bass incoming...', 'Checking the sound...',
  'Warming up the valves...', 'Dub siren activated...', 'Echo chamber loading...',
  'Roots rock rewire...', 'King Tubby style...', 'At the control tower...',
  'Steppers mode engaged...', 'Channel One vibes...', 'Analog warmth rising...',
  'Feed the echo...', 'Spring reverb bouncing...', 'Tape delay spinning...',
];

const GENRE_OPTIONS = ['dub', 'reggae', 'roots', 'dancehall', 'sound system', 'steppers', 'ska', 'rocksteady'];

export function FlyerOcrModal({ onClose, onCreated }: FlyerOcrModalProps) {
  const { t } = useLang();
  const [phase, setPhase] = useState<Phase>('upload');
  const [minimized, setMinimized] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [progressPhase, setProgressPhase] = useState('');
  const [error, setError] = useState('');
  const [ocrData, setOcrData] = useState<OcrData | null>(null);
  const [form, setForm] = useState({
    title: '', date: '', venue_name: '', venue_city: '',
    start_time: '', end_time: '', price_info: '', is_free: false,
    artists: '', genre_tags: ['dub', 'reggae'] as string[], description: '',
  });
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [rotation, setRotation] = useState(0);
  const cropImgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Rotate dub phrases
  useEffect(() => {
    if (phase !== 'analyzing') return;
    const interval = setInterval(() => setPhraseIdx(i => (i + 1) % DUB_PHRASES.length), 2500);
    return () => clearInterval(interval);
  }, [phase]);

  // Clipboard paste
  useEffect(() => {
    if (phase !== 'upload') return;
    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) handleFile(blob);
          break;
        }
      }
    }
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [phase]);

  const handleFile = useCallback((f: File) => {
    setRawFile(f);
    setPreview(URL.createObjectURL(f));
    // Start with crop handles covering the full image — drag corners inward to crop
    setCrop({ x: 0, y: 0, width: 100, height: 100, unit: '%' });
    setRotation(0);
    setPhase('cropping');
  }, []);

  /** Apply crop + rotation on canvas and return a File ready for OCR */
  async function applyCropAndRotation(): Promise<File> {
    const img = cropImgRef.current;
    if (!img) return rawFile!;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // Source crop area, resolved to NATURAL pixels. react-image-crop can hand us
    // a crop in either '%' or 'px' units — the initial full-image selection is in
    // '%', while a user-dragged selection comes back in 'px'. Treating '%' as px
    // (the old bug) grabbed a tiny top-left corner and OCR saw nothing.
    let sx: number, sy: number, sw: number, sh: number;
    if (crop && crop.width && crop.height) {
      if (crop.unit === '%') {
        sx = (crop.x / 100) * img.naturalWidth;
        sy = (crop.y / 100) * img.naturalHeight;
        sw = (crop.width / 100) * img.naturalWidth;
        sh = (crop.height / 100) * img.naturalHeight;
      } else {
        const scaleX = img.naturalWidth / img.width;
        const scaleY = img.naturalHeight / img.height;
        sx = crop.x * scaleX;
        sy = crop.y * scaleY;
        sw = crop.width * scaleX;
        sh = crop.height * scaleY;
      }
    } else {
      // No crop set → use the full image
      sx = 0; sy = 0; sw = img.naturalWidth; sh = img.naturalHeight;
    }

    // Handle rotation: swap dimensions for 90/270
    const isRotated = rotation % 180 !== 0;
    let outW = isRotated ? sh : sw;
    let outH = isRotated ? sw : sh;

    // Resize to max 1200px (saves bandwidth + produces good event card images)
    const MAX = 1200;
    if (outW > MAX || outH > MAX) {
      const scale = MAX / Math.max(outW, outH);
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }

    canvas.width = outW;
    canvas.height = outH;

    const drawScale = outW / (isRotated ? sh : sw);
    ctx.translate(outW / 2, outH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(img, sx, sy, sw, sh,
      -(sw * drawScale) / 2, -(sh * drawScale) / 2,
      sw * drawScale, sh * drawScale);

    return new Promise<File>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(new File([blob!], 'flyer-cropped.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    });
  }

  async function handleCropConfirm() {
    const croppedFile = await applyCropAndRotation();
    setPreview(URL.createObjectURL(croppedFile));
    startAnalysis(croppedFile);
  }

  async function startAnalysis(f: File) {
    setPhase('analyzing');
    setError('');
    setProgress(0);
    setProgressPhase('upload');
    setPhraseIdx(Math.floor(Math.random() * DUB_PHRASES.length));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await api.uploadFlyerWithProgress(
        f,
        (pct, ph) => { setProgress(pct); setProgressPhase(ph); },
        controller.signal,
      );
      setOcrData(result);
      setProgress(100);

      // If server auto-created a pending event, show success directly
      if ((result as any).event_id) {
        setTimeout(() => {
          setPhase('success');
          onCreated(); // refresh the event list
        }, 400);
      } else {
        // Fallback: show review form if no event was created (e.g. no date found)
        setForm({
          title: result.title || '',
          date: result.date || '',
          venue_name: result.venue || (result as any).address || '',
          venue_city: result.city || '',
          start_time: result.start_time || '',
          end_time: result.end_time || '',
          price_info: result.price || '',
          is_free: result.is_free || false,
          artists: (result.artists || []).join(', '),
          genre_tags: detectGenres(result.title, result.artists),
          description: '',
        });
        setTimeout(() => setPhase('results'), 400);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setPhase('upload');
        setProgress(0);
      } else {
        setError(err.message || 'Erreur OCR');
        setPhase('upload');
      }
    } finally {
      abortRef.current = null;
    }
  }

  function handleCancel() {
    if (abortRef.current) abortRef.current.abort();
    setPhase('upload');
    setProgress(0);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) handleFile(f);
  }

  function toggleGenre(g: string) {
    setForm(p => ({ ...p, genre_tags: p.genre_tags.includes(g) ? p.genre_tags.filter(x => x !== g) : [...p.genre_tags, g] }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.date) {
      setSubmitError(
        !form.title && !form.date
          ? t('flyer.missing_title_date')
          : !form.date ? t('flyer.missing_date') : t('flyer.missing_title')
      );
      return;
    }
    setSubmitError('');
    setSaving(true);
    try {
      await api.createEvent({
        ...form,
        image_url: ocrData?.image_url,
        flyer_url: ocrData?.image_url,
        description: form.description || (form.artists ? 'Line-up: ' + form.artists : ''),
        status: 'pending',
        source_type: 'flyer-ocr',
      });
      onCreated();
      onClose();
    } catch (err: any) {
      // Don't swallow failures — a silently dead "Valider" button was the bug.
      const msg = String(err?.message || '');
      setSubmitError(
        /auth|401|expir/i.test(msg) ? t('flyer.session_expired') : (msg || t('flyer.save_failed'))
      );
      console.error('Failed to create event:', err);
    } finally {
      setSaving(false);
    }
  }

  const F = (field: string, value: any) => setForm(p => ({ ...p, [field]: value }));

  const phaseLabel = progressPhase === 'upload' ? t('flyer.upload') : progressPhase === 'ocr' ? 'Multi-Engine OCR' : progressPhase === 'tesseract' ? t('flyer.prescan') : progressPhase === 'vision' ? t('flyer.vision') : progressPhase === 'merging' ? t('flyer.finalizing') : '';

  // ═══ MINIMIZED VIEW ═══
  if (minimized && phase === 'analyzing') {
    return (
      <div style={miniBar} onClick={() => setMinimized(false)}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#c9a84c', letterSpacing: '1px' }}>{t('flyer.scanner')}</span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: i <= Math.floor(progress / 12.5) ? (i < 3 ? '#4caf50' : i < 6 ? '#c9a84c' : '#d4213d') : '#1a1a1a',
              boxShadow: i <= Math.floor(progress / 12.5) ? `0 0 4px ${i < 3 ? '#4caf50' : i < 6 ? '#c9a84c' : '#d4213d'}` : 'none',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>
        <span style={{ fontSize: '11px', color: '#c9a84c', fontStyle: 'italic', flex: 1 }}>{DUB_PHRASES[phraseIdx]}</span>
        <button onClick={(e) => { e.stopPropagation(); handleCancel(); }} style={{ ...btnStyle, padding: '2px 8px', fontSize: '9px', borderColor: '#d4213d', color: '#d4213d' }}>
          STOP
        </button>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={consoleBox} onClick={e => e.stopPropagation()}>
        {/* Screws */}
        <div style={{ ...screwStyle, top: 8, left: 8 }} />
        <div style={{ ...screwStyle, top: 8, right: 8 }} />
        <div style={{ ...screwStyle, bottom: 8, left: 8 }} />
        <div style={{ ...screwStyle, bottom: 8, right: 8 }} />

        {/* Header */}
        <div style={headerStrip}>
          <span style={{ fontSize: '11px', letterSpacing: '3px', fontWeight: 700, color: '#c9a84c' }}>RIDDIM RADAR</span>
          <span style={{ fontSize: '14px', letterSpacing: '4px', fontWeight: 800 }}>{t('flyer.scanner')}</span>
          <span style={{ fontSize: '9px', letterSpacing: '2px', color: '#888' }}>MULTI-ENGINE OCR</span>
        </div>

        {/* ═══ UPLOAD ═══ */}
        {phase === 'upload' && (
          <div style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: '12px', color: '#999', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.6 }}>
              {t('flyer.instructions')}
            </p>

            {error && (
              <div style={{ padding: '8px 12px', marginBottom: '12px', borderRadius: '4px', background: 'rgba(229,57,53,0.1)', border: '1px solid var(--red)', fontSize: '12px', color: 'var(--red)', textAlign: 'center' }}>
                {error}
              </div>
            )}

            <div
              style={{ ...dropZone, borderColor: dragOver ? '#c9a84c' : '#444', background: dragOver ? 'rgba(201,168,76,0.06)' : 'rgba(0,0,0,0.3)' }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={{ fontSize: '40px', marginBottom: '8px', opacity: 0.4 }}>📷</div>
              <div style={{ fontSize: '13px', color: '#aaa', fontWeight: 600 }}>{t('flyer.drop_here')}</div>
              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{t('flyer.click_upload')}</div>
              <div style={{ fontSize: '10px', color: '#555', marginTop: '8px' }}>{t('flyer.paste')}</div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />

            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button onClick={onClose} style={{ ...btnStyle, fontSize: '11px' }}>{t('common.close')}</button>
            </div>
          </div>
        )}

        {/* ═══ CROP & ROTATE ═══ */}
        {phase === 'cropping' && preview && (
          <div style={{ padding: '16px' }}>
            <p style={{ fontSize: '11px', color: '#999', textAlign: 'center', margin: '0 0 12px' }}>
              Crop to the flyer area and rotate if needed
            </p>

            {/* Padding around image so corner handles are reachable on touch */}
            <div style={{ maxHeight: '55vh', overflow: 'auto', display: 'flex', justifyContent: 'center', borderRadius: '6px', background: '#0a0a0a', padding: '16px' }}>
              <ReactCrop crop={crop} onChange={c => setCrop(c)} className="flyer-crop">
                <img
                  ref={cropImgRef}
                  src={preview}
                  alt="Crop"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '50vh',
                    transform: `rotate(${rotation}deg)`,
                    transition: 'transform 0.3s',
                  }}
                />
              </ReactCrop>
            </div>
            <style>{`
              .flyer-crop .ReactCrop__drag-handle { width: 28px !important; height: 28px !important; }
              .flyer-crop .ReactCrop__drag-handle::after {
                width: 14px !important; height: 14px !important;
                border-width: 3px !important; border-color: #c9a84c !important;
              }
            `}</style>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
              <button onClick={() => setRotation(r => (r + 90) % 360)} style={{ ...btnStyle, borderColor: '#c9a84c', color: '#c9a84c' }}>
                ↻ Rotate 90°
              </button>
              <button onClick={() => { setCrop(undefined); setRotation(0); }} style={{ ...btnStyle, fontSize: '10px' }}>
                Reset
              </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
              <button onClick={onClose} style={btnStyle}>
                {t('common.cancel')}
              </button>
              <button onClick={handleCropConfirm} style={{ ...btnStyle, borderColor: '#4caf50', color: '#4caf50', fontWeight: 700 }}>
                &#9658; SCAN
              </button>
            </div>
          </div>
        )}

        {/* ═══ ANALYZING — Dub Console ═══ */}
        {phase === 'analyzing' && (
          <AnalyzingPanel
            preview={preview}
            progress={progress}
            phraseIdx={phraseIdx}
            phaseLabel={phaseLabel}
            onCancel={handleCancel}
            onMinimize={() => setMinimized(true)}
            t={t}
          />
        )}

        {/* ═══ SUCCESS — event auto-created ═══ */}
        {phase === 'success' && (
          <div style={{ padding: '30px 24px', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
              {preview && (
                <img src={preview} alt="Flyer" style={{ width: '160px', maxHeight: '220px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #333' }} />
              )}
              <div>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>&#x2705;</div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#4caf50', letterSpacing: '1px', marginBottom: '6px' }}>
                  Event created for validation
                </div>
                {ocrData?.date && (
                  <div style={{ fontSize: '13px', color: '#aaa', marginBottom: '4px' }}>
                    {ocrData.date} {ocrData.venue ? `@ ${ocrData.venue}` : ''}
                  </div>
                )}
                {ocrData?.artists && ocrData.artists.length > 0 && (
                  <div style={{ fontSize: '11px', color: '#888' }}>
                    {ocrData.artists.slice(0, 5).join(', ')}
                  </div>
                )}
                <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                  Pending admin review in the Validation tab
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button onClick={() => { setPhase('upload'); setPreview(null); setOcrData(null); }} style={{ ...btnStyle, borderColor: '#c9a84c', color: '#c9a84c' }}>
                Scan another flyer
              </button>
              <button onClick={onClose} style={{ ...btnStyle, borderColor: '#4caf50', color: '#4caf50' }}>
                Done
              </button>
            </div>
          </div>
        )}

        {/* ═══ RESULTS (fallback form when no event auto-created) ═══ */}
        {phase === 'results' && (
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div>
                {preview && (
                  <img src={preview} alt="Flyer" style={{ width: '100%', maxWidth: '220px', borderRadius: '6px', border: '1px solid #333', objectFit: 'contain', maxHeight: '360px', background: '#111' }} />
                )}
                <div style={{ textAlign: 'center', marginTop: '6px' }}>
                  <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius: '3px', fontWeight: 700, background: 'rgba(201,168,76,0.15)', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    OCR SCAN
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div style={fg}>
                  <label style={lbl}>{t('add_event.title_label')} *</label>
                  <input style={{ ...inp, ...(form.title ? {} : wb) }} required value={form.title}
                    onChange={e => F('title', e.target.value)} placeholder={t('add_event.event_name')} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div style={fg}>
                    <label style={lbl}>{t('add_event.date_label')} *</label>
                    <input style={{ ...inp, ...(form.date ? {} : wb) }} type="date" required value={form.date}
                      onChange={e => F('date', e.target.value)} />
                  </div>
                  <div style={fg}>
                    <label style={lbl}>{t('add_event.price_label')}</label>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <input style={{ ...inp, flex: 1 }} value={form.price_info}
                        onChange={e => F('price_info', e.target.value)} placeholder="10€" />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: form.is_free ? '#4caf50' : '#666', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={form.is_free} onChange={e => F('is_free', e.target.checked)} /> Free
                      </label>
                    </div>
                  </div>
                  <div style={fg}>
                    <label style={lbl}>{t('add_event.venue_label')}</label>
                    <input style={{ ...inp, ...(form.venue_name ? {} : wb) }} value={form.venue_name}
                      onChange={e => F('venue_name', e.target.value)} placeholder={t('add_event.venue_placeholder')} />
                  </div>
                  <div style={fg}>
                    <label style={lbl}>{t('add_event.city_label')}</label>
                    <input style={inp} value={form.venue_city} onChange={e => F('venue_city', e.target.value)} placeholder={t('add_event.city_placeholder')} />
                  </div>
                  <div style={fg}>
                    <label style={lbl}>{t('flyer.start')}</label>
                    <input style={inp} type="time" value={form.start_time} onChange={e => F('start_time', e.target.value)} />
                  </div>
                  <div style={fg}>
                    <label style={lbl}>{t('flyer.end')}</label>
                    <input style={inp} type="time" value={form.end_time} onChange={e => F('end_time', e.target.value)} />
                  </div>
                </div>
                <div style={fg}>
                  <label style={lbl}>{t('flyer.artists')}</label>
                  <input style={inp} value={form.artists} onChange={e => F('artists', e.target.value)} placeholder="Artist 1, Artist 2" />
                </div>
                <div style={fg}>
                  <label style={lbl}>{t('add_event.genres_label')}</label>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {GENRE_OPTIONS.map(g => (
                      <button key={g} type="button" onClick={() => toggleGenre(g)} style={{
                        padding: '2px 7px', fontSize: '9px', borderRadius: '3px', cursor: 'pointer',
                        border: '1px solid', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px',
                        background: form.genre_tags.includes(g) ? 'rgba(201,168,76,0.15)' : 'transparent',
                        borderColor: form.genre_tags.includes(g) ? '#c9a84c' : '#444',
                        color: form.genre_tags.includes(g) ? '#c9a84c' : '#777',
                      }}>{g}</button>
                    ))}
                  </div>
                </div>
                {/* Explain why the button is inert — a silent disabled button
                    (date not found by OCR) was the original reported bug. */}
                {(!form.title || !form.date) && (
                  <div style={hintBox}>
                    ⚠ {!form.date && !form.title ? t('flyer.missing_title_date')
                       : !form.date ? t('flyer.missing_date') : t('flyer.missing_title')}
                  </div>
                )}
                {submitError && (
                  <div style={errorBox}>{submitError}</div>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={onClose} style={btnStyle}>{t('common.cancel')}</button>
                  <button type="submit" disabled={saving || !form.title || !form.date} style={{
                    ...btnStyle, borderColor: '#4caf50', color: '#4caf50',
                    opacity: (saving || !form.title || !form.date) ? 0.4 : 1,
                  }}>{saving ? t('flyer.saving') : t('flyer.validate')}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Analyzing Panel — Dub Console with irregular animations ─────────────────

const KNOB_LABELS = ['DEPTH', 'RATE', 'FEEDBACK', 'DELAY'];
const SWITCH_LABELS = ['CHANNEL 1', 'SPRING', 'TAPE', 'DUB OUT'];
const ESTIMATED_DURATION = 30; // seconds — progress bar paces to 90% over this

function AnalyzingPanel({ preview, progress, phraseIdx, phaseLabel, onCancel, onMinimize, t }: {
  preview: string | null; progress: number; phraseIdx: number; phaseLabel: string;
  onCancel: () => void; onMinimize: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  // Smooth progress: animate to 90% over estimated duration, then wait for real completion
  const [smoothProgress, setSmoothProgress] = useState(0);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    if (progress >= 90) { setSmoothProgress(progress); return; }
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const target = Math.min(90, (elapsed / ESTIMATED_DURATION) * 90);
      setSmoothProgress(Math.max(target, progress));
    }, 200);
    return () => clearInterval(interval);
  }, [progress]);

  // Irregular knob animation: random target angles, one or two knobs move at a time
  const [knobAngles, setKnobAngles] = useState([0, 0, 0, 0]);
  const [activeKnobs, setActiveKnobs] = useState<Set<number>>(new Set());
  useEffect(() => {
    let mounted = true;
    async function animateKnobs() {
      while (mounted) {
        // Random delay between moves (400-1200ms)
        await new Promise(r => setTimeout(r, 400 + Math.random() * 800));
        if (!mounted) break;
        // Pick 1-2 random knobs to move
        const count = Math.random() > 0.6 ? 2 : 1;
        const indices = new Set<number>();
        while (indices.size < count) indices.add(Math.floor(Math.random() * 4));
        setActiveKnobs(indices);
        setKnobAngles(prev => prev.map((a, i) =>
          indices.has(i) ? (a + 30 + Math.random() * 120) % 360 : a
        ));
        // Hold position briefly
        await new Promise(r => setTimeout(r, 300 + Math.random() * 400));
        if (!mounted) break;
        setActiveKnobs(new Set());
      }
    }
    animateKnobs();
    return () => { mounted = false; };
  }, []);

  // Irregular switch animation: random toggles
  const [switchStates, setSwitchStates] = useState([false, false, false, false]);
  useEffect(() => {
    let mounted = true;
    async function animateSwitches() {
      while (mounted) {
        await new Promise(r => setTimeout(r, 600 + Math.random() * 1400));
        if (!mounted) break;
        const idx = Math.floor(Math.random() * 4);
        setSwitchStates(prev => prev.map((s, i) => i === idx ? !s : s));
      }
    }
    animateSwitches();
    return () => { mounted = false; };
  }, []);

  const ledCount = Math.floor(smoothProgress / 8.5);

  return (
    <div style={{ padding: '16px' }}>
      {/* Responsive layout: stack on mobile */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
        {preview && (
          <img src={preview} alt="Flyer" style={{
            width: '140px', maxHeight: '200px', objectFit: 'contain',
            borderRadius: '6px', border: '1px solid #333', flexShrink: 0,
          }} />
        )}

        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={consolePanel}>
            {/* Knobs row */}
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: '14px' }}>
              {KNOB_LABELS.map((label, i) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <DubKnob angle={knobAngles[i]} moving={activeKnobs.has(i)} />
                  <div style={{ fontSize: '7px', color: '#999', letterSpacing: '1.5px', marginTop: '4px', fontWeight: 700 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* LED strip */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginBottom: '12px' }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  background: i <= ledCount ? (i < 5 ? '#4caf50' : i < 9 ? '#c9a84c' : '#d4213d') : '#1a1a1a',
                  boxShadow: i <= ledCount
                    ? `0 0 6px ${i < 5 ? '#4caf50' : i < 9 ? '#c9a84c' : '#d4213d'}`
                    : 'inset 0 1px 2px rgba(0,0,0,0.5)',
                  transition: 'all 0.4s',
                }} />
              ))}
            </div>

            {/* VU meter progress bar */}
            <div style={vuMeterTrack}>
              <div style={{ ...vuMeterFill, width: `${smoothProgress}%` }} />
            </div>

            {/* Switch row */}
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '12px' }}>
              {SWITCH_LABELS.map((label, i) => (
                <DubSwitch key={label} label={label} isOn={switchStates[i]} done={smoothProgress >= 95} />
              ))}
            </div>
          </div>

          {/* Phase label */}
          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '10px', color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {phaseLabel}
          </div>

          {/* Dub phrase */}
          <div style={{ marginTop: '6px', textAlign: 'center', fontSize: '13px', color: '#c9a84c', fontStyle: 'italic', fontWeight: 500, letterSpacing: '0.5px', minHeight: '20px' }}>
            {DUB_PHRASES[phraseIdx]}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
            <button onClick={onCancel} style={{ ...btnStyle, borderColor: '#d4213d', color: '#d4213d' }}>
              {t('common.cancel')}
            </button>
            <button onClick={onMinimize} style={{ ...btnStyle, borderColor: '#c9a84c', color: '#c9a84c' }}>
              {t('flyer.minimize')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Dub Console Components ──────────────────────────────────────────────────

function DubKnob({ angle, moving }: { angle: number; moving: boolean }) {
  return (
    <div style={{
      width: '40px', height: '40px', borderRadius: '50%',
      background: 'radial-gradient(circle at 40% 35%, #444 0%, #1a1a1a 60%, #111 100%)',
      border: '2px solid #555',
      boxShadow: moving
        ? 'inset 0 2px 4px rgba(0,0,0,0.6), 0 0 8px rgba(201,168,76,0.3), 0 0 0 1px rgba(255,255,255,0.05)'
        : 'inset 0 2px 4px rgba(0,0,0,0.6), 0 2px 4px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)',
      position: 'relative',
      transform: `rotate(${angle}deg)`,
      transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
    }}>
      <div style={{
        position: 'absolute', top: '4px', left: '50%',
        width: '2px', height: '10px', marginLeft: '-1px',
        background: moving ? '#c9a84c' : '#666',
        borderRadius: '1px',
        boxShadow: moving ? '0 0 4px #c9a84c' : 'none',
        transition: 'background 0.3s, box-shadow 0.3s',
      }} />
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: '7px', height: '7px', marginLeft: '-3.5px', marginTop: '-3.5px',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 35%, #888 0%, #333 100%)',
        border: '1px solid #555',
      }} />
    </div>
  );
}

function DubSwitch({ label, isOn, done }: { label: string; isOn: boolean; done: boolean }) {
  const active = done || isOn;
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '14px', height: '24px', borderRadius: '7px', margin: '0 auto',
        position: 'relative', cursor: 'default',
        background: done
          ? 'linear-gradient(180deg, #4caf50 0%, #2d7a2d 100%)'
          : active
            ? 'linear-gradient(180deg, #c9a84c 0%, #8b7530 100%)'
            : 'linear-gradient(180deg, #333 0%, #1a1a1a 100%)',
        border: `1px solid ${done ? '#5ccc60' : active ? '#c9a84c' : '#555'}`,
        boxShadow: active
          ? `inset 0 1px 1px rgba(255,255,255,0.15), 0 0 6px ${done ? 'rgba(76,175,80,0.5)' : 'rgba(201,168,76,0.4)'}`
          : 'inset 0 1px 1px rgba(255,255,255,0.1), 0 2px 3px rgba(0,0,0,0.5)',
        transition: 'all 0.4s',
      }}>
        <div style={{
          position: 'absolute', width: '10px', height: '10px', borderRadius: '50%',
          left: '1px', top: active ? '2px' : '10px',
          background: 'linear-gradient(145deg, #fff 0%, #d0d0d0 40%, #a8a8a8 70%)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.5)',
          transition: 'top 0.4s',
        }} />
      </div>
      <div style={{
        fontSize: '6px', letterSpacing: '0.5px', marginTop: '3px', fontWeight: 700,
        color: done ? '#4caf50' : active ? '#c9a84c' : '#555',
        transition: 'color 0.3s',
      }}>{label}</div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const consoleBox: React.CSSProperties = {
  maxWidth: '820px', width: '95vw', maxHeight: '90vh', overflowY: 'auto',
  background: 'linear-gradient(180deg, #1a1a1e 0%, #131316 40%, #0e0e12 100%)',
  borderRadius: '12px', border: '2px solid #2a2a30',
  boxShadow: '0 20px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.5)',
  position: 'relative',
};

const miniBar: React.CSSProperties = {
  position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1001,
  display: 'flex', alignItems: 'center', gap: '12px',
  padding: '8px 16px', borderRadius: '8px',
  background: 'linear-gradient(180deg, #1a1a1e 0%, #0e0e12 100%)',
  border: '1px solid #2a2a30',
  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
  cursor: 'pointer', minWidth: '280px', maxWidth: '95vw',
};

const headerStrip: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
  padding: '14px 20px 10px', borderBottom: '1px solid #2a2a30',
  background: 'linear-gradient(180deg, rgba(201,168,76,0.04) 0%, transparent 100%)',
};

const screwStyle: React.CSSProperties = {
  position: 'absolute', width: '10px', height: '10px', borderRadius: '50%',
  background: 'radial-gradient(circle at 35% 35%, #555 0%, #222 60%, #111 100%)',
  border: '1px solid #333',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.05)',
  zIndex: 10,
};

const consolePanel: React.CSSProperties = {
  padding: '18px 16px 14px',
  borderRadius: '8px',
  background: 'linear-gradient(180deg, #1e1e22 0%, #141418 50%, #0c0c10 100%)',
  border: '1px solid #333',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 0 rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.5)',
};

const dropZone: React.CSSProperties = {
  border: '2px dashed #444', borderRadius: '8px',
  padding: '40px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
};

const vuMeterTrack: React.CSSProperties = {
  position: 'relative', height: '12px', borderRadius: '6px',
  background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
  border: '1px solid #333', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6)', overflow: 'hidden',
};

const vuMeterFill: React.CSSProperties = {
  height: '100%', borderRadius: '5px',
  background: 'linear-gradient(90deg, #4caf50 0%, #8bc34a 30%, #c9a84c 60%, #ff9800 80%, #d4213d 100%)',
  boxShadow: '0 0 8px rgba(201,168,76,0.4)', transition: 'width 0.5s ease-out',
};

const btnStyle: React.CSSProperties = {
  padding: '6px 16px', fontSize: '11px', fontWeight: 700,
  background: 'transparent', border: '1px solid #555', borderRadius: '4px',
  color: '#aaa', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px',
};

const fg: React.CSSProperties = { marginBottom: '6px' };
const lbl: React.CSSProperties = { display: 'block', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#c9a84c', marginBottom: '2px' };
const inp: React.CSSProperties = { width: '100%', padding: '5px 8px', fontSize: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid #333', borderRadius: '4px', color: '#e0e0e0', outline: 'none', boxSizing: 'border-box' };
const wb: React.CSSProperties = { borderColor: 'rgba(201,168,76,0.4)' };
const hintBox: React.CSSProperties = {
  marginTop: '10px', padding: '7px 10px', borderRadius: '4px', fontSize: '11px',
  background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.4)',
  color: '#c9a84c', textAlign: 'center',
};
const errorBox: React.CSSProperties = {
  marginTop: '10px', padding: '7px 10px', borderRadius: '4px', fontSize: '11px',
  background: 'rgba(229,57,53,0.12)', border: '1px solid var(--red)',
  color: 'var(--red)', textAlign: 'center',
};

function detectGenres(title?: string, artists?: string[]): string[] {
  const text = ((title || '') + ' ' + (artists || []).join(' ')).toLowerCase();
  const genres: string[] = [];
  if (/\bdub\b/.test(text)) genres.push('dub');
  if (/\breggae\b/.test(text)) genres.push('reggae');
  if (/\broots\b/.test(text)) genres.push('roots');
  if (/\bdancehall\b/.test(text)) genres.push('dancehall');
  if (/\bstepper/.test(text)) genres.push('steppers');
  if (/\bska\b/.test(text)) genres.push('ska');
  if (/\brocksteady\b/.test(text)) genres.push('rocksteady');
  if (/sound\s*system/.test(text)) genres.push('sound system');
  if (genres.length === 0) genres.push('dub', 'reggae');
  return genres;
}
